import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';

const Ng = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGuestBook = async () => {
      try {
        const response = await fetch('https://api.ngmc.co/v1/guest-book?limit=100');
        if (!response.ok) throw new Error('Error ):');
        
        const data = await response.json();
        
        // Sort the entries based on upvotes (highest first) instead of score
        const sortedByUpvotes = data.sort((a, b) => b.upvotes - a.upvotes);
        
        setEntries(sortedByUpvotes);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchGuestBook();
  }, []);

  // Filter entries based on the search query (by name or xuid)
  const filteredEntries = entries.filter((entry) => {
    const query = searchQuery.toLowerCase();
    return (
      entry.player.name.toLowerCase().includes(query) ||
      entry.player.xuid.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-900 via-black to-black text-white">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-10 h-10 border-4 border-gray-600 border-t-white rounded-full animate-spin"></div>
          <p className="text-xl text-gray-400 font-light tracking-widest">LOADING...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-900 via-black to-black text-red-400">
        <p className="border border-red-500/30 bg-red-500/10 px-6 py-4 rounded-xl">
          Error: {error}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-black text-gray-200 p-4 md:p-8 font-sans selection:bg-gray-700">
      <div className="max-w-7xl mx-auto">
        
        <header className="mb-10 text-center mt-6">
          <h1 className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-b from-white via-gray-200 to-gray-500 mb-4 tracking-tight">
            NetherGames Guest Book
          </h1>
          <p className="text-gray-500 uppercase tracking-[0.2em] text-sm font-semibold">
            Most liked Entires First
          </p>
        </header>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12 relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            className="w-full bg-[#161616] border border-[#333] text-gray-200 rounded-xl py-3 pl-12 pr-4 focus:outline-none focus:border-gray-500 focus:ring-1 focus:ring-gray-500 transition-all shadow-[0_4px_20px_rgb(0,0,0,0.5)] placeholder-gray-500"
            placeholder="Search by player name or XUID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Entries Grid */}
        {filteredEntries.length === 0 ? (
          <div className="text-center text-gray-500 mt-10">
            <p className="text-xl">No players found matching "{searchQuery}"</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEntries.map((entry) => (
              <div 
                key={entry.xuid} 
                className="bg-gradient-to-b from-[#161616] to-[#0a0a0a] rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.8)] border border-[#222] hover:border-[#444] hover:-translate-y-1 transition-all duration-300 flex flex-col h-full group"
              >
                
                {/* Card Header: Avatar & Name & Votes */}
                <div className="flex justify-between items-start mb-5">
                  <div className="flex items-center space-x-4 text-white">
                    <div className="relative">
                      <img
                        src={`https://cdn.nethergames.org/skins/${entry.player.skinHash}/head.png`}
                        alt={`${entry.player.name}'s avatar`}
                        className="w-12 h-12 rounded-lg bg-black border border-gray-800 shadow-md group-hover:border-gray-500 transition-colors"
                        loading="lazy"
                        style={{ imageRendering: 'pixelated' }}
                      />
                      {/* Subtle glow effect behind avatar */}
                      <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 blur-md rounded-lg transition-opacity duration-300 -z-10"></div>
                    </div>
                    
                    <div>
                      {/* Clickable Name */}
                      <h3 
                        onClick={() => navigate(`/players/${entry.player.name}`)}
                        className="font-bold text-lg text-gray-100 tracking-wide cursor-pointer hover:text-white hover:underline underline-offset-4 decoration-gray-500 transition-all"
                      >
                        {entry.player.name}
                      </h3>
                      <p className="text-xs text-gray-500 font-mono uppercase tracking-wider opacity-80 mt-0.5">
                        ID: {entry.player.xuid.substring(0, 8)}
                      </p>
                    </div>
                  </div>

                  {/* Vote Counters */}
                  <div className="flex space-x-2 text-xs font-bold">
                    <span className="flex items-center text-green-400 bg-green-500/10 border border-green-500/20 px-2.5 py-1.5 rounded-lg shadow-sm">
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.514" /></svg>
                      {entry.upvotes}
                    </span>
                    {/*<span className="flex items-center text-red-400 bg-red-500/10 border border-red-500/20 px-2.5 py-1.5 rounded-lg shadow-sm">
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.514" /></svg>
                      {entry.downvotes}
                    </span>*/}
                  </div>
                </div>

                {/* Card Body: The message text */}
                <div className="flex-grow">
                  <p className="text-gray-400 text-sm whitespace-pre-wrap leading-relaxed font-medium group-hover:text-gray-300 transition-colors">
                    {entry.text}
                  </p>
                </div>
                
              </div>
            ))}
          </div>
        )}

      </div>
      <div className="flex justify-center items-center text-gray-400 mt-2 mb-2">
      <p className="text-red-400 mt-2 mb-2">Currently only showing 100 entires due to NG api limitations ):</p>
      </div>
    </div>
  );
};

export default Ng;
